import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Loader2, Check, ArrowLeft, Plus, Trash2, FileInput, AlertCircle, X, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { aiAPI, jobsAPI } from '../services/api';
import { DatePicker } from '../components/DatePicker';

const EMPTY_PREVIEW = {
  po_number: '', po_reference: '', po_date: '',
  client_name: '', client_email: '', client_phone: '', client_company: '', client_abn: '',
  client_address: '', site_address: '',
  customer_name: '', customer_email: '', customer_phone: '',
  project_name: '', scope_description: '',
  start_date: '', end_date: '',
  payment_terms: '', delivery_terms: '',
  items: [],
  subtotal: 0, tax: 0, total: 0,
  notes: '',
};

const UNITS = ['Item', 'm2', 'lm', 'pc', 'ps', 'hour', 'day', 'sqm', 'm', 'kg', 'L', 'set', 'lot', 'allow'];
const TRADES = ['General', 'Plumber', 'Electrician', 'Carpenter', 'Painter', 'Tiler', 'Roofer', 'Plasterer', 'Bricklayer', 'Landscaper', 'HVAC', 'Glazier', 'Flooring', 'Demolition', 'Concrete'];

export default function ImportFromPOPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const assessmentRef = useRef(null);
  const [step, setStep] = useState(1); // 1=upload, 2=review, 3=done
  const [file, setFile] = useState(null);
  const [assessmentFile, setAssessmentFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(EMPTY_PREVIEW);
  const [result, setResult] = useState(null);
  const [duplicate, setDuplicate] = useState(null); // {existing_job_id, existing_job_title, message}

  const recalcTotals = (items) => {
    const subtotal = items.reduce((s, i) => s + (parseFloat(i.quantity || 0) * parseFloat(i.unit_price || 0)), 0);
    const tax = subtotal * 0.1;
    return { subtotal: +subtotal.toFixed(2), tax: +tax.toFixed(2), total: +(subtotal + tax).toFixed(2) };
  };

  const handleExtract = async () => {
    if (!file) {
      toast.error('Please select a PDF');
      return;
    }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await aiAPI.importPoPdf(fd);
      if (data.status === 'partial') {
        toast.warning('AI could only partially parse the PO — please review carefully');
      } else {
        toast.success('PO extracted — please review and adjust');
      }
      const items = (data.items || []).map(i => ({
        description: i.description || '',
        quantity: i.quantity || 1,
        unit: i.unit || 'Item',
        unit_price: i.unit_price || 0,
        trade_type: i.trade_type || 'General',
      }));
      setPreview({ ...EMPTY_PREVIEW, ...data, items });
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to extract PO. Ensure the PDF is readable.');
    } finally {
      setExtracting(false);
    }
  };

  const updateField = (key, val) => setPreview(p => ({ ...p, [key]: val }));

  const updateItem = (idx, key, val) => {
    setPreview(p => {
      const items = [...p.items];
      items[idx] = { ...items[idx], [key]: val };
      return { ...p, items, ...recalcTotals(items) };
    });
  };

  const addItem = () => {
    const items = [...preview.items, { description: '', quantity: 1, unit: 'Item', unit_price: 0, trade_type: 'General' }];
    setPreview(p => ({ ...p, items, ...recalcTotals(items) }));
  };

  const removeItem = (idx) => {
    const items = preview.items.filter((_, i) => i !== idx);
    setPreview(p => ({ ...p, items, ...recalcTotals(items) }));
  };

  const handleCommit = async (force = false) => {
    if (!preview.client_name?.trim()) {
      toast.error('Client name is required');
      return;
    }
    if (!preview.items.length) {
      toast.error('At least one line item is required');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify(preview));
      if (file) fd.append('file', file);
      if (assessmentFile) fd.append('assessment_report', assessmentFile);
      if (force) fd.append('force', 'true');
      const { data } = await jobsAPI.importFromPo(fd);
      setResult(data);
      setStep(3);
      toast.success(`Job, quote, and invoice created!`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && typeof detail === 'object' && detail?.code === 'duplicate_po') {
        setDuplicate(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create job');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="import-from-po-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')} data-testid="back-to-jobs-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileInput className="h-7 w-7 text-primary" />
            Import Job from Supplied PO
          </h1>
          <p className="text-muted-foreground mt-1">
            Upload a client-issued PO — AI extracts the details, you review, and it becomes a Job + Quote + Invoice. Optionally attach an assessment report at the same time.
          </p>
        </div>
      </div>

      <div className="space-y-6 mt-2">

      {/* Step indicator */}
      <div className="flex items-center gap-4 text-sm">
        {['Upload PDF', 'Review & Edit', 'Done'].map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center border ${step > i ? 'bg-green-500/20 border-green-500 text-green-400' : step === i + 1 ? 'bg-primary/20 border-primary text-primary' : 'border-border text-muted-foreground'}`}>
              {step > i + 1 ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={step === i + 1 ? 'font-medium' : 'text-muted-foreground'}>{label}</span>
            {i < 2 && <span className="text-muted-foreground mx-2">→</span>}
          </div>
        ))}
      </div>

      {/* STEP 1: Upload */}
      {step === 1 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Upload the Purchase Order</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-border rounded-sm p-16 text-center cursor-pointer hover:border-primary transition-colors"
              data-testid="po-upload-dropzone"
            >
              <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              {file ? (
                <div>
                  <FileText className="h-5 w-5 inline mr-2 text-primary" />
                  <span className="font-medium">{file.name}</span>
                  <p className="text-xs text-muted-foreground mt-2">Click to change</p>
                </div>
              ) : (
                <div>
                  <p className="text-lg font-medium">Drop your PO PDF here or click to browse</p>
                  <p className="text-sm text-muted-foreground mt-1">PDF files only</p>
                </div>
              )}
              <input
                type="file"
                ref={fileRef}
                accept="application/pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files[0])}
                data-testid="po-file-input"
              />
            </div>

            {/* Optional: Assessment Report upload */}
            <div>
              <Label className="flex items-center gap-2 text-sm font-medium mb-2">
                <ClipboardCheck className="h-4 w-4 text-primary" />
                Assessment Report <span className="text-xs font-normal text-muted-foreground">(optional — saved to this job's Documents)</span>
              </Label>
              <div
                onClick={() => assessmentRef.current?.click()}
                className="border-2 border-dashed border-border rounded-sm p-6 text-center cursor-pointer hover:border-primary transition-colors"
                data-testid="po-assessment-dropzone"
              >
                {assessmentFile ? (
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-primary" />
                    <span className="font-medium">{assessmentFile.name}</span>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={(e) => { e.stopPropagation(); setAssessmentFile(null); }}
                      data-testid="po-clear-assessment-btn"
                    ><X className="h-4 w-4" /></Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Click to attach an assessment report PDF (optional)</p>
                )}
                <input
                  type="file"
                  ref={assessmentRef}
                  accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  onChange={(e) => setAssessmentFile(e.target.files[0])}
                  data-testid="po-assessment-file-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => navigate('/jobs')} data-testid="po-cancel-btn">Cancel</Button>
              <Button onClick={handleExtract} disabled={!file || extracting} data-testid="po-extract-btn">
                {extracting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Extracting…</> : 'Extract with AI'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2: Review */}
      {step === 2 && (
        <div className="space-y-6">
          <Card className="bg-card border-border" data-testid="po-client-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Client <span className="text-xs font-normal text-muted-foreground">(issued the PO — invoice will be addressed here)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>PO Number</Label><Input value={preview.po_number || ''} onChange={(e) => updateField('po_number', e.target.value)} data-testid="po-number-input" /></div>
              <div><Label>PO Date</Label><DatePicker value={preview.po_date?.slice(0, 10) || ''} onChange={(d) => updateField('po_date', d)} placeholder="PO date" data-testid="po-date-picker" /></div>
              <div><Label>Client Reference</Label><Input value={preview.po_reference || ''} onChange={(e) => updateField('po_reference', e.target.value)} /></div>
              <div><Label>Client ABN</Label><Input value={preview.client_abn || ''} onChange={(e) => updateField('client_abn', e.target.value)} /></div>
              <div><Label>Client Name / Company *</Label><Input value={preview.client_name || ''} onChange={(e) => updateField('client_name', e.target.value)} data-testid="po-client-name-input" /></div>
              <div><Label>Client Email</Label><Input type="email" value={preview.client_email || ''} onChange={(e) => updateField('client_email', e.target.value)} data-testid="po-client-email-input" /></div>
              <div><Label>Client Phone</Label><Input value={preview.client_phone || ''} onChange={(e) => updateField('client_phone', e.target.value)} /></div>
              <div><Label>Billing Address</Label><Input value={preview.client_address || ''} onChange={(e) => updateField('client_address', e.target.value)} /></div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border" data-testid="po-customer-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Customer <span className="text-xs font-normal text-muted-foreground">(end-user / site contact — not billed, for your records)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Customer Name</Label><Input value={preview.customer_name || ''} onChange={(e) => updateField('customer_name', e.target.value)} data-testid="po-customer-name-input" placeholder="e.g. homeowner at the site" /></div>
              <div><Label>Customer Email</Label><Input type="email" value={preview.customer_email || ''} onChange={(e) => updateField('customer_email', e.target.value)} data-testid="po-customer-email-input" /></div>
              <div><Label>Customer Phone</Label><Input value={preview.customer_phone || ''} onChange={(e) => updateField('customer_phone', e.target.value)} data-testid="po-customer-phone-input" /></div>
              <div className="md:col-span-2"><Label>Site Address</Label><Input value={preview.site_address || ''} onChange={(e) => updateField('site_address', e.target.value)} data-testid="po-site-address-input" /></div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader><CardTitle>Project Details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><Label>Project / Job Title *</Label><Input value={preview.project_name || ''} onChange={(e) => updateField('project_name', e.target.value)} data-testid="po-project-name-input" /></div>
              <div><Label>Start Date</Label><DatePicker value={preview.start_date?.slice(0, 10) || ''} onChange={(d) => updateField('start_date', d)} placeholder="Start date" /></div>
              <div><Label>End Date</Label><DatePicker value={preview.end_date?.slice(0, 10) || ''} onChange={(d) => updateField('end_date', d)} placeholder="End date" /></div>
              <div className="md:col-span-2"><Label>Scope of Works</Label><Textarea rows={3} value={preview.scope_description || ''} onChange={(e) => updateField('scope_description', e.target.value)} /></div>
              <div><Label>Payment Terms</Label><Input value={preview.payment_terms || ''} onChange={(e) => updateField('payment_terms', e.target.value)} /></div>
              <div><Label>Delivery Terms</Label><Input value={preview.delivery_terms || ''} onChange={(e) => updateField('delivery_terms', e.target.value)} /></div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Line Items ({preview.items.length})</CardTitle>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="po-add-item-btn"><Plus className="h-4 w-4 mr-1" />Add Row</Button>
            </CardHeader>
            <CardContent>
              {preview.items.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No items extracted. Add some manually.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground">
                        <th className="text-left py-2 pr-2">Description</th>
                        <th className="text-right py-2 px-2 w-24">Qty</th>
                        <th className="text-left py-2 px-2 w-28">Unit</th>
                        <th className="text-right py-2 px-2 w-32">Unit Price</th>
                        <th className="text-left py-2 px-2 w-32">Trade</th>
                        <th className="text-right py-2 px-2 w-28">Total</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.items.map((item, idx) => {
                        const qty = parseFloat(item.quantity || 0);
                        const price = parseFloat(item.unit_price || 0);
                        return (
                          <tr key={idx} className="border-b border-border/50">
                            <td className="py-1 pr-2"><Input value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} data-testid={`po-item-desc-${idx}`} /></td>
                            <td className="py-1 px-2"><Input type="number" step="0.01" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} className="text-right" /></td>
                            <td className="py-1 px-2">
                              <select value={item.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value)} className="w-full h-9 bg-input border border-border rounded-sm px-2 text-sm">
                                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                            </td>
                            <td className="py-1 px-2"><Input type="number" step="0.01" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} className="text-right" /></td>
                            <td className="py-1 px-2">
                              <select value={item.trade_type} onChange={(e) => updateItem(idx, 'trade_type', e.target.value)} className="w-full h-9 bg-input border border-border rounded-sm px-2 text-sm">
                                {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </td>
                            <td className="py-1 px-2 text-right font-medium">${(qty * price).toFixed(2)}</td>
                            <td className="py-1 px-2"><Button size="icon" variant="ghost" onClick={() => removeItem(idx)} data-testid={`po-remove-item-${idx}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="text-sm"><td colSpan={5} className="py-2 text-right">Subtotal:</td><td className="py-2 px-2 text-right font-medium">${preview.subtotal?.toFixed(2)}</td><td></td></tr>
                      <tr className="text-sm"><td colSpan={5} className="py-1 text-right">GST (10%):</td><td className="py-1 px-2 text-right font-medium">${preview.tax?.toFixed(2)}</td><td></td></tr>
                      <tr className="text-base"><td colSpan={5} className="py-2 text-right font-bold">Total:</td><td className="py-2 px-2 text-right font-bold text-primary">${preview.total?.toFixed(2)}</td><td></td></tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="po-back-btn"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
            <Button onClick={handleCommit} disabled={saving} data-testid="po-commit-btn">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</> : <><Check className="h-4 w-4 mr-2" />Create Job + Quote + Invoice</>}
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3: Done */}
      {step === 3 && result && (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
              <Check className="h-8 w-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold" data-testid="po-success-heading">Successfully Imported!</h2>
            <div className="space-y-1 text-muted-foreground">
              <p><strong className="text-foreground">Job:</strong> {result.job_title}</p>
              <p><strong className="text-foreground">Quote:</strong> {result.quote_number}</p>
              <p><strong className="text-foreground">Invoice:</strong> {result.invoice_number}</p>
              {result.document_id && (
                <p className="text-sm flex items-center justify-center gap-1.5 text-green-400">
                  <FileText className="h-4 w-4" />
                  PO PDF attached to job's Documents tab
                </p>
              )}
              <p className="text-lg mt-2"><strong className="text-foreground">Total:</strong> ${result.total?.toFixed(2)}</p>
            </div>
            <div className="flex gap-2 justify-center pt-4">
              <Button variant="outline" onClick={() => { setStep(1); setFile(null); setPreview(EMPTY_PREVIEW); setResult(null); }} data-testid="po-import-another-btn">Import Another</Button>
              <Button onClick={() => navigate(`/jobs/${result.job_id}`)} data-testid="po-view-job-btn">View Job</Button>
            </div>
          </CardContent>
        </Card>
      )}
      </div>

      {/* Duplicate PO dialog */}
      <Dialog open={!!duplicate} onOpenChange={(v) => !v && setDuplicate(null)}>
        <DialogContent data-testid="po-duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-destructive" /> Duplicate Purchase Order</DialogTitle>
          </DialogHeader>
          <p className="text-sm">{duplicate?.message}</p>
          {duplicate?.existing_job_title && (
            <p className="text-sm text-muted-foreground mt-2">Existing job: <span className="font-medium text-foreground">{duplicate.existing_job_title}</span></p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDuplicate(null)}>Cancel</Button>
            {duplicate?.existing_job_id && (
              <Button variant="outline" onClick={() => navigate(`/jobs/${duplicate.existing_job_id}`)} data-testid="po-view-existing-job-btn">View Existing Job</Button>
            )}
            <Button variant="destructive" onClick={() => { setDuplicate(null); handleCommit(true); }} data-testid="po-force-create-btn">Create Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
